import type { MouseEvent } from 'react'
import { useState } from 'react'

import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'

import type { MijlpaalColor } from '../types/Mijlpalen'

export const colorStyles: Record<MijlpaalColor, string> = {
  red: 'red',
  amber: 'darkorange',
  green: 'green',
  grey: 'grey',
}

const colorLabels: Record<MijlpaalColor, string> = {
  red: 'Laat mijlpalen zien die te laat zijn',
  amber: 'Laat mijlpalen zien die bijna te laat zijn',
  green: 'Laat mijlpalen zijn die behaald zijn',
  grey: 'Laat openstaande mijlpalen zien die niet (bijna) te laat zijn',
}

const colors: MijlpaalColor[] = ['red', 'amber', 'green', 'grey']

interface Props {
  color: MijlpaalColor | null
  onChange: (color: MijlpaalColor | null) => void
}

export const MijlpaalColorPicker = ({ color, onChange }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClick = (clickedColor: MijlpaalColor) => {
    onChange(clickedColor === color ? null : clickedColor)
    handleClose()
  }

  return (
    <>
      <Tooltip title="Status mijlpalen">
        <IconButton
          data-testid="mijlpaal-color-button"
          aria-label="Status mijlpalen"
          sx={{ color: color ? colorStyles[color] : undefined }}
          aria-haspopup="true"
          onClick={handleOpen}
        >
          <ErrorRoundedIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={handleClose}
        disableRestoreFocus
      >
        <Stack direction="row">
          {colors.map((code) => (
            <Tooltip key={code} title={colorLabels[code]}>
              <IconButton
                data-testid={`mijlpaal-color-${code}-button`}
                onClick={() => handleClick(code)}
              >
                <ErrorRoundedIcon sx={{ color: colorStyles[code] }} />
              </IconButton>
            </Tooltip>
          ))}
        </Stack>
      </Popover>
    </>
  )
}
